import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { TextInput } from '_/views/budget-view-v2/components/Inputs/TextInput'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput'
import Autocomplete from '_components/inputs/Autocomplete'
import IconButton from '_/components/svg/icon-button'

import { Trash } from '@refera/ui-icons'
import AddIcon from '@mui/icons-material/Add'
import Theme from '@refera/ui-core'
import { Button } from '@refera/ui-web'
import { useDialog } from '_/hooks/use-dialog'
import useStyles from '../Accordions/styles'
import { useInspectionLoading } from '_/views/inspection-budget/hooks/use-inspection-loading'
import { getInspectionBudgetResolutionMaterialUnidsSelector } from '_/modules/inspectionBudget/selector'
import { useExecutionDueUpdate } from '../../hooks/use-execution-due-update'
import { useLaborValueUpdate } from '../../hooks/use-labor-value-update'
import { toNumeric } from '_/views/inspection-budget/utils/functions'

const getUnitLabel = option => {
  if (typeof option === 'string') return option
  return option?.unid || option?.label || ''
}

const getUnitValue = value => {
  if (typeof value === 'number') return value
  if (typeof value === 'string') return value
  return value?.id ?? value?.value
}

export function MaterialsTable({ name, compartmentIndex, isReadOnly = false }) {
  const styles = useStyles()
  const { closeDialog, showDialog } = useDialog()
  const { budgetType } = useInspectionLoading()
  const materialUnids = useSelector(getInspectionBudgetResolutionMaterialUnidsSelector)
  const isLoadingMaterialUnids = !materialUnids

  const { control, setValue, getValues } = useFormContext()
  const { getAndUpdateLaborValue } = useLaborValueUpdate({ getValues, setValue, budgetType })
  const { updateExecutionDue } = useExecutionDueUpdate({ getValues, setValue })
  const baseName = useMemo(() => name.replace(/\.budgetResolutionMaterials$/, ''), [name])
  const initialQtyRef = useRef(null)
  const initialLaborTimeRef = useRef(null)

  const { fields, append, remove } = useFieldArray({
    control,
    name,
    keyName: 'fieldId',
  })

  const materials = useWatch({
    control,
    name,
    defaultValue: fields,
  })
  const totalMaterialsQty = useMemo(
    () =>
      (materials || []).reduce((sum, material) => {
        return sum + toNumeric(material?.quantity)
      }, 0),
    [materials]
  )
  const currentLaborTime = useWatch({
    control,
    name: `${baseName}.laborTime`,
  })
  const { derivedCompartmentIndex, derivedResolutionIndex } = useMemo(() => {
    const indexes = (String(baseName).match(/\d+/g) || []).map(Number)
    const resolution = indexes.length > 0 ? indexes[indexes.length - 1] : 0
    const compartment = indexes.length > 1 ? indexes[indexes.length - 2] : 0

    return {
      derivedCompartmentIndex: Number.isFinite(compartment) ? compartment : 0,
      derivedResolutionIndex: Number.isFinite(resolution) ? resolution : 0,
    }
  }, [baseName])

  useEffect(() => {
    const normalizedCurrentQty = toNumeric(totalMaterialsQty)
    if (
      (initialQtyRef.current == null || initialQtyRef.current === 0) &&
      normalizedCurrentQty > 0
    ) {
      initialQtyRef.current = normalizedCurrentQty
    }
    const normalizedLaborTime = toNumeric(currentLaborTime)

    if (
      (initialLaborTimeRef.current == null || initialLaborTimeRef.current === 0) &&
      normalizedLaborTime > 0
    ) {
      initialLaborTimeRef.current = normalizedLaborTime
    }
  }, [totalMaterialsQty, currentLaborTime])

  const materialUnidsOptions = Array.isArray(materialUnids?.results)
    ? materialUnids.results
    : Array.isArray(materialUnids)
    ? materialUnids
    : []

  const handleRemove = useCallback(
    index => {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja excluir esse registro?',
        labelApprove: 'Confirmar',
        labelCancel: 'Cancelar',
        onCancel: closeDialog,
        onApprove: () => {
          remove(index)
          closeDialog()
        },
      })
    },
    [showDialog, closeDialog, remove]
  )

  const setRowTotal = useCallback(
    (index, overrides = {}) => {
      const quantity = overrides.quantity ?? toNumeric(getValues(`${name}.${index}.quantity`) ?? 0)
      const unitPrice =
        overrides.unitPrice ?? toNumeric(getValues(`${name}.${index}.unitPrice`) ?? 0)

      const nextTotal = Number((quantity * unitPrice).toFixed(2))
      setValue(`${name}.${index}.total`, nextTotal, { shouldDirty: false, shouldValidate: false })
    },
    [getValues, name, setValue]
  )

  const handleUnitPriceBlur = useCallback(
    (e, index) => {
      const fieldName = e.target.name
      const { value } = e.target

      const numericValue = value ? toNumeric(value) : 0
      setValue(fieldName, numericValue, { shouldValidate: true, shouldDirty: true })
      setRowTotal(index, { unitPrice: numericValue })
    },
    [setRowTotal, setValue]
  )

  const handleQtyBlur = useCallback(
    (e, index) => {
      const fieldName = e.target.name
      const { value } = e.target

      const numericValue = value ? toNumeric(value) : 0
      setValue(fieldName, numericValue, { shouldValidate: true, shouldDirty: true })
      setRowTotal(index, { quantity: numericValue })

      const nextMaterials = (materials || []).map((material, materialIndex) =>
        materialIndex === index ? { ...material, quantity: numericValue } : material
      )
      const nextTotalQty = nextMaterials.reduce((sum, material) => {
        return sum + toNumeric(material?.quantity)
      }, 0)

      const baseQty = initialQtyRef.current || nextTotalQty || 1
      const baseLaborTime = initialLaborTimeRef.current || 0
      const newLaborTime = baseLaborTime * (nextTotalQty / baseQty)
      const normalizedLaborTime = Number(newLaborTime.toFixed(2))
      const normalizedCompartmentIndex = Number(compartmentIndex)
      const targetCompartmentIndex = Number.isFinite(normalizedCompartmentIndex)
        ? normalizedCompartmentIndex
        : derivedCompartmentIndex

      setValue(`${baseName}.laborTime`, normalizedLaborTime, {
        shouldValidate: true,
        shouldDirty: true,
      })

      getAndUpdateLaborValue({
        compartmentIndex: targetCompartmentIndex,
        resolutionIndex: derivedResolutionIndex,
        laborTime: normalizedLaborTime,
      })
      updateExecutionDue()
    },
    [
      baseName,
      compartmentIndex,
      derivedCompartmentIndex,
      derivedResolutionIndex,
      getAndUpdateLaborValue,
      materials,
      setValue,
      setRowTotal,
      updateExecutionDue,
    ]
  )

  return (
    <div>
      {fields?.map((field, index) => {
        return (
          <div key={field.fieldId} className={styles.materialValuesContainer}>
            <div
              className={styles.numberInput}
              style={{
                marginTop: index === 0 ? '14px' : '-8px',
              }}
            >
              {index + 1}
            </div>
            <TextInput
              name={`${name}.${index}.description`}
              label={index === 0 ? 'Material' : ''}
              placeholder="Material"
              defaultValue={field?.description}
              disabled={isReadOnly}
              style={{ minWidth: '150px' }}
            />

            <Autocomplete
              name={`${name}.${index}.unid`}
              label={index === 0 ? 'Unid.' : ''}
              required
              options={materialUnidsOptions}
              multiple={false}
              loading={isLoadingMaterialUnids}
              defaultValue={field?.unid ?? field?.unit ?? null}
              getOptionLabel={getUnitLabel}
              getOptionSelected={(option, value) => option.id === getUnitValue(value)}
              autocompleteClasses={`${styles.compactAutocomplete} ${styles.unidAutocompleteSpacing}`}
              disabled={isReadOnly}
              enableContainsSearch
            />

            <NumericInput
              name={`${name}.${index}.quantity`}
              label={index === 0 ? 'Qtd.' : ''}
              placeholder="0"
              defaultValue={field?.quantity}
              disabled={isReadOnly}
              onBlur={e => handleQtyBlur(e, index)}
            />

            <NumericInput
              name={`${name}.${index}.unitPrice`}
              label={index === 0 ? 'Valor' : ''}
              placeholder="R$ 0,00"
              prefix="R$ "
              defaultValue={field?.unitPrice}
              disabled={isReadOnly}
              onBlur={e => handleUnitPriceBlur(e, index)}
            />

            <NumericInput
              name={`${name}.${index}.total`}
              label={index === 0 ? 'Total' : ''}
              placeholder="R$ 0,00"
              prefix="R$ "
              disabled
              defaultValue={field?.total}
            />
            <div className={styles.actions}>
              <IconButton
                variant="ghost"
                onClick={() => handleRemove(index)}
                disabled={isReadOnly}
                style={{ marginTop: index === 0 ? '14px' : '-8px' }}
              >
                <Trash
                  color={isReadOnly ? Theme.Colors.Grayscale.ThirtyTwo : Theme.Colors.Red.Base}
                  fontSize={20}
                />
              </IconButton>
              {index === 0 && (
                <IconButton
                  variant="ghost"
                  className={styles.iconButton}
                  onClick={() =>
                    append({ description: '', unid: null, quantity: 0, unitPrice: 0, total: 0 })
                  }
                  disabled={isReadOnly}
                  style={{ marginTop: index === 0 ? '14px' : '-8px' }}
                >
                  <AddIcon
                    style={{
                      color: isReadOnly
                        ? Theme.Colors.Grayscale.ThirtyTwo
                        : Theme.Colors.Primary.Base,
                    }}
                    className={styles.editIcon}
                  />
                </IconButton>
              )}
            </div>
          </div>
        )
      })}
      {fields?.length === 0 && (
        <Button
          variant="secondary"
          onClick={() =>
            append({ description: '', unid: null, quantity: 0, unitPrice: 0, total: 0 })
          }
          disabled={isReadOnly}
          style={{ width: '200px', marginTop: '8px' }}
        >
          + Adicionar material
        </Button>
      )}
    </div>
  )
}
